/**
 * InactivityTimer
 * Pure class — no React dependencies — tracks user inactivity and fires a callback.
 * Activity is throttled to avoid excessive storage writes.
 * Uses sessionStorage so the timer persists across soft reloads but not across browser closes.
 */

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const
const LAST_ACTIVITY_KEY = 'spcc-last-activity'
const DEFAULT_THROTTLE_MS = 30_000 // only update storage every 30 s

export interface InactivityTimerConfig {
  timeoutMs: number
  onTimeout: () => void
  throttleMs?: number
}

export class InactivityTimer {
  private readonly timeoutMs: number
  private readonly onTimeout: () => void
  private readonly throttleMs: number
  private intervalId: ReturnType<typeof setInterval> | null = null
  private lastWriteTs = 0
  private boundHandleActivity: () => void
  private boundHandleVisibility: () => void

  constructor(config: InactivityTimerConfig) {
    this.timeoutMs = config.timeoutMs
    this.onTimeout = config.onTimeout
    this.throttleMs = config.throttleMs ?? DEFAULT_THROTTLE_MS

    this.boundHandleActivity = () => this.handleActivity()
    this.boundHandleVisibility = () => this.handleVisibilityChange()
  }

  start(): void {
    // Prevent duplicate listeners if start() is called multiple times
    if (this.intervalId !== null) return

    this.writeActivity()

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, this.boundHandleActivity, { passive: true })
    }
    document.addEventListener('visibilitychange', this.boundHandleVisibility)

    this.intervalId = setInterval(() => this.checkTimeout(), 60_000)
  }

  stop(): void {
    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, this.boundHandleActivity)
    }
    document.removeEventListener('visibilitychange', this.boundHandleVisibility)

    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  reset(): void {
    this.writeActivity()
  }

  getTimeRemaining(): number {
    const lastActivity = this.readActivity()
    const elapsed = Date.now() - lastActivity
    return Math.max(0, this.timeoutMs - elapsed)
  }

  private handleActivity(): void {
    const now = Date.now()
    if (now - this.lastWriteTs >= this.throttleMs) {
      this.writeActivity()
    }
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.checkTimeout()
    }
  }

  private checkTimeout(): void {
    const lastActivity = this.readActivity()
    if (Date.now() - lastActivity > this.timeoutMs) {
      this.stop()
      this.onTimeout()
    }
  }

  private writeActivity(): void {
    const now = Date.now()
    this.lastWriteTs = now
    try {
      sessionStorage.setItem(LAST_ACTIVITY_KEY, String(now))
    } catch {
      // Ignore storage errors (e.g., private browsing quota)
    }
  }

  private readActivity(): number {
    try {
      const raw = sessionStorage.getItem(LAST_ACTIVITY_KEY)
      if (raw) return parseInt(raw, 10)
    } catch {
      // Ignore storage errors
    }
    return Date.now()
  }
}
